import React from 'react';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import { Tier1Plan } from '../../types/tier1Portfolio';

interface Props {
  plan: Tier1Plan;
  title?: string;
}

const Tier1PlanDossier: React.FC<Props> = ({ plan, title = 'Investment plan (AI review)' }) => {
  const author =
    plan.planAuthor === 'ai_review'
      ? 'Account-aware AI review'
      : plan.planAuthor === 'deterministic_fallback'
        ? 'Deterministic fallback'
        : null;

  const rationales = plan.rationaleBySleeve ?? {};
  const sleeveKeys = Object.keys(rationales).sort();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-600" aria-hidden />
          {title}
          {author ? (
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-normal ${
                plan.planAuthor === 'ai_review'
                  ? 'bg-violet-100 text-violet-800'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {author}
            </span>
          ) : null}
          {plan.weightsSource ? (
            <span className="text-xs font-normal text-gray-500">
              Macro weights: {plan.weightsSource}
            </span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-gray-700">
        {plan.summary ? (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Summary</p>
            <p className="mt-1 leading-relaxed">{plan.summary}</p>
          </div>
        ) : null}
        {plan.accountContext ? (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Account context
            </p>
            <p className="mt-1 leading-relaxed">{plan.accountContext}</p>
          </div>
        ) : null}
        {plan.risks && plan.risks.length > 0 ? (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Risks</p>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {plan.risks.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {sleeveKeys.length > 0 ? (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Sleeve rationale
            </p>
            <ul className="mt-2 space-y-2">
              {sleeveKeys.map((key) => (
                <li key={key} className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                  <span className="font-medium text-gray-900">{key.replace(/_/g, ' ')}</span>
                  <p className="mt-0.5 text-gray-600">{rationales[key]}</p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {plan.aiPlan?.fallbackReason ? (
          <p className="text-xs text-amber-800">
            AI review unavailable: {plan.aiPlan.fallbackReason}
          </p>
        ) : null}
        {plan.aiPlan?.model ? (
          <p className="text-xs text-gray-400">Model: {plan.aiPlan.model}</p>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default Tier1PlanDossier;
